import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import analyticsDashboardApi from "../services/analyticsDashboard.service";

// Ported from iventas-analytics/apps/analytics-dashboard/src/hooks/useDataEnrichment.ts
// Adapted to react-query v3 (host app version) and plain JS.
const useAnalyticsDataEnrichment = (companyId) => {
  const {
    data: usersResponse,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery(
    ["analytics-users", companyId],
    () => analyticsDashboardApi.getUsers(companyId),
    { enabled: !!companyId, retry: 1, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, refetchOnMount: false }
  );

  const {
    data: formsResponse,
    isLoading: formsLoading,
    error: formsError,
  } = useQuery(
    ["analytics-leadgen-forms", companyId],
    () => analyticsDashboardApi.getLeadGenForms(companyId),
    { enabled: !!companyId, retry: 1, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, refetchOnMount: false }
  );

  const {
    data: channelsResponse,
    isLoading: channelsLoading,
    error: channelsError,
  } = useQuery(
    ["analytics-active-channels", companyId],
    () => analyticsDashboardApi.getActiveChannels(companyId),
    { enabled: !!companyId, retry: 1, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, refetchOnMount: false }
  );

  const users = useMemo(() => usersResponse?.data || [], [usersResponse]);
  const leadGenForms = useMemo(() => formsResponse?.data || [], [formsResponse]);
  const channels = useMemo(() => channelsResponse?.data || [], [channelsResponse]);
  const isLoading = usersLoading || formsLoading || channelsLoading;
  const error = usersError?.message || formsError?.message || channelsError?.message || null;

  const enrichmentMaps = useMemo(() => {
    const userMap = new Map();
    const formMap = new Map();
    const channelMap = new Map();

    users.forEach((user) => userMap.set(user.id, user.name));
    leadGenForms.forEach((form) => formMap.set(form.id, form.name));
    channels.forEach((channel) => channelMap.set(channel.id, channel.name));

    return { userMap, formMap, channelMap };
  }, [users, leadGenForms, channels]);

  const getUserName = useCallback(
    (userId) => enrichmentMaps.userMap.get(userId) || userId,
    [enrichmentMaps]
  );

  const getFormName = useCallback(
    (formId) => enrichmentMaps.formMap.get(formId) || formId,
    [enrichmentMaps]
  );

  const getChannelName = useCallback(
    (channelId) => enrichmentMaps.channelMap.get(channelId) || channelId,
    [enrichmentMaps]
  );

  return useMemo(
    () => ({
      users,
      leadGenForms,
      channels,
      isLoading,
      error,
      getUserName,
      getFormName,
      getChannelName,
      isReady: !isLoading && !error,
      hasData: users.length > 0 || leadGenForms.length > 0 || channels.length > 0,
    }),
    [users, leadGenForms, channels, isLoading, error, getUserName, getFormName, getChannelName]
  );
};

export default useAnalyticsDataEnrichment;
